import * as React from 'react'
import styled from 'styled-components'
import { get, clone, setWith } from 'lodash'
import {
  Colors,
  Inset,
  Media,
  Spacing,
  StackChildren,
} from '@doordash/design-language'
import { NumberField } from '@doordash/component-fields'
import Separator from '@doordash/component-separator'
import ListCell from '@doordash/component-list-cell'
import Button, { IconButton } from '@doordash/component-button'
import { CountryCode } from '@doordash/lib-i18n'
import { OnboardingComponentProps } from '.'
import { BankAccountSetup as Tag } from '../../../models/selectorAnchors'
import { NoBankAccountModal } from '../add-bank-account/Countries/USA/NoBankAccountModal'
import Form from '../../../utilities/Form'
import t from '../add-bank-account/strings'
import { Stripe } from '../add-bank-account/AddBankAccount'
import { loadStripe } from '../../atoms/stripe'

const FormSeparator = styled(Separator)`
  ${Media.Mobile`
    width: 100vw;
    margin-left: -${Spacing.XSmall}px;
  `}
`

const StyledListCellMenuItem = styled.div`
  &:hover {
    cursor: pointer;
    background-color: ${Colors.ButtonFlatPrimaryBackgroundHovered};
  }
`

type PaymentData = {
  country?: CountryCode | ''
  currency?: string
  routing_number: string
  account_number: string
  account_holder_name?: string
  account_holder_type?: string
}

export const BankAccountFormComponent: React.FC<OnboardingComponentProps> = React.memo(
  ({
    stripeCredentials,
    values,
    templateResponseData,
    apiPath,
    onSubmitStep,
  }) => {
    const [stripe, setStripe] = React.useState<Stripe>(null)
    const [accountNumberErrors, setAccountNumberErrors] = React.useState<
      string
    >()
    const [routingNumberErrors, setRoutingNumberErrors] = React.useState<
      string
    >()
    const [isNoBankModalShowing, setIsNoBankModalShowing] = React.useState<
      boolean
    >(false)

    const fields = {
      routingNumber: '',
      accountNumber: '',
    }

    const validators = {
      accountNumber: error => (!error ? accountNumberErrors : ''),
      routingNumber: error => (!error ? routingNumberErrors : ''),
    }

    React.useEffect(() => {
      if (stripeCredentials && stripeCredentials.publicKey) {
        loadStripe(setStripe, stripeCredentials.publicKey)
      }
    }, [stripeCredentials])

    const handleNoBankClick = (e: React.ChangeEvent) => {
      e.preventDefault()
      setIsNoBankModalShowing(true)
    }

    const setBankAccountFormErrors = () => {
      const errorMessage = t.errorMessageRoutingAccount()

      setRoutingNumberErrors(errorMessage)
      setAccountNumberErrors(errorMessage)
    }

    const handleSubmit = async data => {
      setAccountNumberErrors('')
      setRoutingNumberErrors('')

      const { accountNumber, routingNumber } = data
      const name = get(values, 'name', '')
      const countryShortname = get(stripeCredentials, 'countryShortname', '')
      const currencyCode = get(stripeCredentials, 'currencyCode', '')

      if (accountNumber === '' || routingNumber === '') {
        setBankAccountFormErrors()
        return
      }

      const payload: PaymentData = {
        country: countryShortname,
        currency: currencyCode,
        routing_number: routingNumber,
        account_number: accountNumber,
        account_holder_name: name,
        account_holder_type: 'individual',
      }

      try {
        const bankToken = await stripe.createToken('bank_account', payload)
        if (bankToken.error) {
          throw bankToken.error
        }

        const updatedTemplateResponseData = setWith(
          clone(templateResponseData),
          apiPath,
          bankToken.token,
          clone
        )

        onSubmitStep(updatedTemplateResponseData)
      } catch (e) {
        setBankAccountFormErrors()

        if (e.type !== 'invalid_request_error') {
          // throw an error for sentry to catch
          throw e
        }
      }
    }

    return (
      <React.Fragment>
        <Form onSubmit={handleSubmit} validators={validators} fields={fields}>
          {(fields, handleChange, handleSubmit, { isLoading }) => (
            <form onSubmit={handleSubmit}>
              <StackChildren size={StackChildren.Sizes.Medium}>
                <NumberField
                  label={t.routingNumber()}
                  onChange={value => {
                    handleChange('routingNumber', value)
                  }}
                  placeholder={''}
                  {...fields.routingNumber}
                  error={routingNumberErrors}
                  data-anchor-id={Tag.RoutingNumber1}
                />

                <NumberField
                  label={t.checkingAccountNumber()}
                  onChange={value => {
                    handleChange('accountNumber', value)
                  }}
                  placeholder={''}
                  {...fields.accountNumber}
                  error={accountNumberErrors}
                  data-anchor-id={Tag.CheckingAccountNumber1}
                />

                <FormSeparator />

                <StyledListCellMenuItem>
                  <ListCell
                    title={t.noBankTitle()}
                    size={ListCell.Sizes.Small}
                    iconEnd={{
                      type: ListCell.IconTypes.ChevronRight,
                      accessibilityLabel: `Chevron Right`,
                      size: IconButton.Sizes.Medium,
                      color: Colors.TextTertiary,
                    }}
                    insetSize={Inset.Sizes.Small}
                    separatorType={ListCell.SeparatorTypes.FullBorder}
                    titleMaxLines={2}
                    onClick={handleNoBankClick}
                    data-anchor-id={Tag.NoBankAccountButton}
                  />
                </StyledListCellMenuItem>

                <StackChildren size={StackChildren.Sizes.XxSmall}>
                  <Button
                    onClick={async () => {
                      await handleSubmit()
                    }}
                    state={
                      isLoading ? Button.States.Loading : Button.States.Default
                    }
                    data-anchor-id={Tag.PrimaryAction}
                  >
                    {t.submit()}
                  </Button>
                </StackChildren>
              </StackChildren>
            </form>
          )}
        </Form>

        {isNoBankModalShowing && (
          <NoBankAccountModal
            onClose={() => {
              setIsNoBankModalShowing(false)
            }}
          />
        )}
      </React.Fragment>
    )
  }
)
